import { defaultInterest } from '../../domain/lifecycleCalc';
import type { CallLine, RegistryState } from '../../data/types';
import { money } from '../../lib/format';
import { newId } from '../../lib/random';
import type { RegistryAction } from '../actions';
import {
  addApproval,
  addCommunication,
  addDocuments,
  fundCode,
  holderName,
  today,
  withAudit,
} from '../helpers';

type Handler<T extends RegistryAction['type']> = (
  state: RegistryState,
  action: Extract<RegistryAction, { type: T }>,
) => RegistryState;

function patchLine(
  state: RegistryState,
  callId: string,
  holderId: string,
  patch: Partial<CallLine>,
): RegistryState {
  return {
    ...state,
    calls: state.calls.map((call) =>
      call.id === callId
        ? {
            ...call,
            lines: call.lines.map((line) =>
              line.holderId === holderId ? { ...line, ...patch } : line,
            ),
          }
        : call,
    ),
  };
}

export const issueDefaultNotice: Handler<'CALL_DEFAULT_NOTICE'> = (state, action) => {
  const call = state.calls.find((item) => item.id === action.callId);
  const line = call?.lines.find((item) => item.holderId === action.holderId);
  if (!call || !line) return state;
  const code = fundCode(state, call.fundId);
  let next = patchLine(state, call.id, line.holderId, {
    defaultStage: 'NOTICE_ISSUED',
    noticeDate: today,
  });
  next = addDocuments(next, [
    {
      holderId: line.holderId,
      fundId: call.fundId,
      kind: 'DEFAULT_NOTICE',
      name: `${code} Call ${call.number} default notice`,
      date: today,
      sizeKb: 64,
      visibleToInvestor: true,
      refId: call.id,
    },
  ]);
  next = addCommunication(next, {
    template: 'DEFAULT_NOTICE',
    subject: `Notice of default: ${code} Call ${call.number}`,
    audience: holderName(state, line.holderId),
    fundId: call.fundId,
    holderIds: [line.holderId],
    channels: ['EMAIL', 'PUSH', 'POST'],
  });
  return withAudit(
    next,
    'Default notice issued',
    `${code} Call ${call.number}`,
    `${holderName(state, line.holderId)} · ${money(line.amount - line.paid)} outstanding`,
  );
};

export const waiveDefaultInterest: Handler<'CALL_DEFAULT_WAIVE'> = (state, action) => {
  const call = state.calls.find((item) => item.id === action.callId);
  const line = call?.lines.find((item) => item.holderId === action.holderId);
  if (!call || !line) return state;
  const waived = defaultInterest(line, call.dueDate, today);
  return withAudit(
    patchLine(state, call.id, line.holderId, { interestWaived: true }),
    'Default interest waived',
    `${fundCode(state, call.fundId)} Call ${call.number}`,
    `${holderName(state, line.holderId)} · ${money(waived, true)}`,
  );
};

export const proposeDefaultRemedy: Handler<'CALL_DEFAULT_REMEDY'> = (state, action) => {
  const call = state.calls.find((item) => item.id === action.callId);
  const line = call?.lines.find((item) => item.holderId === action.holderId);
  if (!call || !line) return state;
  let next = patchLine(state, call.id, line.holderId, {
    defaultStage: 'REMEDY_PROPOSED',
    remedy: action.remedy,
  });
  next = addApproval(next, {
    kind: 'CALL_DEFAULT',
    refId: `${call.id}|${line.holderId}`,
    fundId: call.fundId,
    title: `Default remedy: ${holderName(state, line.holderId)}, ${fundCode(state, call.fundId)} Call ${call.number}`,
    summary: `${action.remedy}. ${money(line.amount - line.paid)} unpaid after the cure period.`,
    amount: line.amount - line.paid,
  });
  return withAudit(
    next,
    'Default remedy proposed',
    `${fundCode(state, call.fundId)} Call ${call.number}`,
    action.remedy,
  );
};

export const updatePaymentException: Handler<'PAYMENT_EXCEPTION_UPDATE'> = (state, action) => {
  const exception = state.paymentExceptions.find((item) => item.id === action.id);
  if (!exception) return state;
  let next: RegistryState = {
    ...state,
    paymentExceptions: state.paymentExceptions.map((item) =>
      item.id === action.id
        ? {
            ...item,
            status: action.status,
            resolvedAt:
              action.status === 'REISSUED' || action.status === 'UNCLAIMED'
                ? today
                : item.resolvedAt,
          }
        : item,
    ),
  };
  if (
    action.status === 'AWAITING_INVESTOR' &&
    !state.tasks.some((task) => task.refId === exception.id && task.status === 'OPEN')
  ) {
    next = {
      ...next,
      tasks: [
        {
          id: newId('t'),
          holderId: exception.holderId,
          kind: 'CONFIRM_BANK',
          title: 'Confirm your bank account',
          detail: `We couldn't pay ${exception.eventLabel.toLowerCase()} (${money(exception.amount, true)}). Confirm your bank details and we'll pay it straight away.`,
          status: 'OPEN',
          refId: exception.id,
        },
        ...next.tasks,
      ],
    };
  }
  const labels = {
    OPEN: 'reopened',
    AWAITING_INVESTOR: 'investor asked to confirm bank details',
    REISSUED: 'payment reissued',
    UNCLAIMED: 'lodged as unclaimed money',
  };
  return withAudit(
    next,
    `Payment exception: ${labels[action.status]}`,
    holderName(state, exception.holderId),
    `${exception.eventLabel} · ${money(exception.amount, true)}`,
  );
};

export const recordRemittance: Handler<'REMITTANCE_RECORD'> = (state, action) => {
  const remittance = state.remittances.find((item) => item.id === action.id);
  if (!remittance) return state;
  return withAudit(
    {
      ...state,
      remittances: state.remittances.map((item) =>
        item.id === action.id ? { ...item, remittedAt: today, reference: action.reference } : item,
      ),
    },
    'Withholding remitted to ATO',
    remittance.month,
    action.reference,
  );
};

export const sendCommunication: Handler<'COMMUNICATION_SEND'> = (state, action) =>
  withAudit(
    addCommunication(state, action.log),
    'Communication sent',
    action.log.subject,
    `${action.log.holderIds.length} recipients · ${action.log.audience}`,
  );
